import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '@ui/components/icon';

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './pagination.component.html',
})
export class PaginationComponent {
  total = input.required<number>();
  next = input<{ limit: number; offset: number } | null>();
  previous = input<{ limit: number; offset: number } | null>();
  currentOffset = input<number>(0);
  currentLimit = input<number>(10);

  pageChange = output<{ limit: number; offset: number }>();

  protected readonly pagesToShow = 5;
  protected readonly rowsPerPageOptions = [10, 20, 30, 40, 50];
  protected rowsPerPage = computed(() => this.currentLimit());

  totalPages = computed(() => Math.ceil(this.total() / this.rowsPerPage()));
  currentPage = computed(() => {
    const offset = this.currentOffset();
    const rowsPerPage = this.rowsPerPage();
    return Math.floor(offset / rowsPerPage) + 1;
  });

  pages = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const pagesToShow = this.pagesToShow;

    if (total <= pagesToShow) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    if (current <= 3) {
      return [1, 2, 3, 4, '...', total];
    }

    if (current > total - 3) {
      return [1, '...', total - 3, total - 2, total - 1, total];
    }

    return [1, '...', current - 1, current, current + 1, '...', total];
  });

  goToPage(page: number | '...') {
    if (page === '...') {
      return;
    }
    const targetPage = page as number;
    this.emitPageChange(
      this.rowsPerPage(),
      (targetPage - 1) * this.rowsPerPage(),
    );
  }

  onRowsPerPageChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.emitPageChange(Number(target.value), 0);
  }

  previousPage() {
    if (this.previous()) {
      const currentPage = this.currentPage();
      this.emitPageChange(
        this.rowsPerPage(),
        (currentPage - 2) * this.rowsPerPage(),
      );
    }
  }

  nextPage() {
    if (this.next()) {
      this.emitPageChange(
        this.rowsPerPage(),
        this.currentPage() * this.rowsPerPage(),
      );
    }
  }

  private emitPageChange(limit: number, offset: number) {
    this.pageChange.emit({
      limit: limit,
      offset: offset,
    });
  }
}
